import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import type { SchedulingScope } from '../hooks/use-scheduling-scope';

/**
 * The provider / appointment-type / location picker, driven by a
 * {@link SchedulingScope}. Shared by the text-schedule drawer and the /sms/test
 * tool — and the workflow builder's test sheet (ADR-306) — so all three fields
 * (and their "required to offer slots" contract) live in one component. The voice call drawer renders its own richer variant (with
 * EHR-mapping warnings + slot preview) but sources state from the same hook.
 */
export function SchedulingScopeFields({
  scope,
  idPrefix,
  providerHint = "The agent offers only this provider's times.",
}: {
  scope: SchedulingScope;
  idPrefix: string;
  /** The line under the provider; null for none (e.g. an appointment that already exists). */
  providerHint?: string | null;
}): React.ReactElement {
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-provider`}>Provider</Label>
        <Select value={scope.providerId} onValueChange={scope.setProviderId}>
          <SelectTrigger id={`${idPrefix}-provider`} className="w-full">
            <SelectValue placeholder="Select a provider" />
          </SelectTrigger>
          <SelectContent>
            {scope.providers.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {providerHint && <p className="text-muted-foreground text-xs">{providerHint}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-type`}>Appointment type</Label>
        <Select value={scope.appointmentTypeId} onValueChange={scope.setAppointmentTypeId}>
          <SelectTrigger id={`${idPrefix}-type`} className="w-full">
            <SelectValue placeholder="Select an appointment type" />
          </SelectTrigger>
          <SelectContent>
            {scope.appointmentTypes.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
                <span className="text-muted-foreground ml-2 text-xs">{t.durationMinutes} min</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {scope.locationOptions.length > 1 && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-location`}>Location</Label>
          <Select value={scope.locationId} onValueChange={scope.setLocationId}>
            <SelectTrigger id={`${idPrefix}-location`} className="w-full">
              <SelectValue placeholder="Select a location" />
            </SelectTrigger>
            <SelectContent>
              {scope.locationOptions.map((l) => (
                <SelectItem key={l.location.id} value={l.location.id}>
                  {l.location.name}
                  {l.isPrimary ? (
                    <span className="text-muted-foreground ml-2 text-xs">primary</span>
                  ) : null}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  );
}
